import type { MaybeRefOrGetter } from 'vue'
import type { ChartPoint, ChartSeries } from '@/lib/charts/types'
import type { CustomerUsageRow, CustomerUsageSeries } from '@/types'
import { computed, toValue } from 'vue'

const COLORS = [
  'var(--ui-primary)',
  '#16a34a',
  '#dc2626',
  '#9333ea',
  '#ca8a04',
  '#0891b2',
]

const TOP_CUSTOMERS = 6

export function usagePoint(bucket: string, value: number): ChartPoint {
  return { x: new Date(bucket).getTime(), y: value }
}

export function totalUsagePoints(series?: CustomerUsageSeries): ChartPoint[] {
  const buckets = series?.months ?? []
  return buckets.map(bucket => usagePoint(
    bucket,
    (series?.customers ?? []).reduce((sum, customer) => sum + (customer.values[bucket] ?? 0), 0),
  ))
}

export function sumUsagePoints(points: ChartPoint[]): number {
  return points.reduce((sum, point) => sum + (point.y ?? 0), 0)
}

// Builds the area-plus-top-customer-lines chart series shared by the product
// usage chart and the dashboard's billable-usage chart: one total series plus
// one line per top customer, all sharing the same bucket boundaries.
export function useCustomerUsageChartSeries(series: MaybeRefOrGetter<CustomerUsageSeries | undefined>, totalLabel: string) {
  const visibleCustomers = computed<CustomerUsageRow[]>(() => toValue(series)?.customers.slice(0, TOP_CUSTOMERS) ?? [])

  const totalSeries = computed<ChartSeries>(() => ({
    id: 'total',
    label: totalLabel,
    color: COLORS[0],
    type: 'area',
    points: totalUsagePoints(toValue(series)),
  }))

  const customerSeries = computed<ChartSeries[]>(() => {
    const buckets = toValue(series)?.months ?? []

    return visibleCustomers.value.map((customer, index) => ({
      id: `customer-${customer.customer_id}`,
      label: customer.customer_name,
      color: COLORS[(index + 1) % COLORS.length],
      type: 'line',
      points: buckets.map(bucket => usagePoint(bucket, customer.values[bucket] ?? 0)),
    }))
  })

  const chartSeries = computed<ChartSeries[]>(() => [totalSeries.value, ...customerSeries.value])

  const hasData = computed(() => chartSeries.value.some(entry => entry.points.some(point => (point.y ?? 0) > 0)))

  const total = computed(() => sumUsagePoints(totalSeries.value.points))

  return { totalSeries, customerSeries, chartSeries, hasData, total }
}
